// Hand-rolled, dependency-free SVG/HTML charts sized to the admin design
// tokens. Line/area charts stretch to fill width via preserveAspectRatio=none
// with non-scaling strokes (so lines stay crisp); bar/stacked/funnel charts are
// plain HTML for sharp text and effortless reflow.

import { el, fmtCompact } from './dom';

const SVG_NS = 'http://www.w3.org/2000/svg';

function svg(
  tag: string,
  attrs: Record<string, string | number> = {},
  children: (SVGElement | string)[] = [],
): SVGElement {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  for (const c of children) {
    node.append(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return node;
}

export interface SeriesPoint {
  label: string;
  value: number;
}

export interface LineChartOptions {
  /** Accessible/legend label for the series. */
  name?: string;
  /** CSS color for the line + area (defaults to the accent). */
  color?: string;
  /** Format a value for the hover tooltip. */
  format?: (n: number) => string;
  /** Format a value for the y-axis tick labels (defaults to compact: "12K"). */
  axisFormat?: (n: number) => string;
  /** Hide the y-axis entirely (rare — defaults to shown). */
  hideYAxis?: boolean;
}

// Viewbox coordinate space. The 5:1.4 ratio keeps charts ~200px tall inside the
// 1200px content column; preserveAspectRatio=none stretches x/y independently.
const VB_W = 1000;
const VB_H = 280;
const PAD = { top: 12, right: 6, bottom: 6, left: 6 };

/**
 * Area + line chart that fills its container width at a fixed height. Renders an
 * invisible hover band per point carrying a native `<title>` tooltip.
 */
export function lineAreaChart(
  points: SeriesPoint[],
  opts: LineChartOptions = {},
): HTMLElement {
  const color = opts.color ?? 'var(--accent)';
  const fmt = opts.format ?? ((n) => String(n));
  const axisFmt = opts.axisFormat ?? fmtCompact;
  const innerW = VB_W - PAD.left - PAD.right;
  const innerH = VB_H - PAD.top - PAD.bottom;
  const n = points.length;
  const maxV = Math.max(1, ...points.map((p) => p.value));

  const xAt = (i: number): number =>
    PAD.left + (n <= 1 ? innerW / 2 : (i / (n - 1)) * innerW);
  const yAt = (v: number): number => PAD.top + innerH - (v / maxV) * innerH;

  const linePts = points.map((p, i) => `${xAt(i)},${yAt(p.value)}`).join(' ');
  const areaPts =
    n > 0
      ? `${PAD.left + (n <= 1 ? innerW / 2 : 0)},${PAD.top + innerH} ` +
        linePts +
        ` ${xAt(n - 1)},${PAD.top + innerH}`
      : '';

  // Y-axis ticks: 4 evenly-spaced fractions of the series max. The same
  // fractions drive both the gridlines (drawn inside the SVG) and the tick
  // labels (drawn as HTML alongside it, since non-uniform viewBox scaling —
  // preserveAspectRatio=none — would distort SVG <text> glyphs) so the two
  // stay pixel-aligned.
  const axisFractions = [0.25, 0.5, 0.75, 1];
  const gridlines = axisFractions.map((f) =>
    svg('line', {
      x1: PAD.left,
      x2: PAD.left + innerW,
      y1: PAD.top + innerH - f * innerH,
      y2: PAD.top + innerH - f * innerH,
      class: 'chart-grid',
    }),
  );

  const kids: SVGElement[] = [...gridlines];
  if (n > 0) {
    kids.push(
      svg('polygon', { points: areaPts, class: 'chart-area', fill: color }),
      svg('polyline', {
        points: linePts,
        class: 'chart-line',
        stroke: color,
      }),
    );
    // Invisible hover bands with native tooltips. (No endpoint dot — the
    // non-uniform stretch would deform a circle into an ellipse.)
    const bandW = n > 1 ? innerW / (n - 1) : innerW;
    points.forEach((p, i) => {
      const rect = svg('rect', {
        x: xAt(i) - bandW / 2,
        y: PAD.top,
        width: bandW,
        height: innerH,
        fill: 'transparent',
      });
      rect.append(svg('title', {}, [`${p.label}: ${fmt(p.value)}`]));
      kids.push(rect);
    });
  }

  const chartSvg = svg(
    'svg',
    {
      viewBox: `0 0 ${VB_W} ${VB_H}`,
      preserveAspectRatio: 'none',
      class: 'chart-svg',
      role: 'img',
      'aria-label': opts.name ?? 'chart',
    },
    kids,
  );

  const first = points[0]?.label ?? '';
  const lastLabel = points[n - 1]?.label ?? '';

  const showYAxis = !opts.hideYAxis && n > 0;
  const yAxisLabels = showYAxis
    ? el(
        'div',
        { class: 'chart-yaxis' },
        axisFractions.map((f) => {
          const topPct = ((PAD.top + innerH - f * innerH) / VB_H) * 100;
          return el(
            'span',
            { class: 'chart-yaxis-tick', style: `top: ${topPct}%` },
            [axisFmt(f * maxV)],
          );
        }),
      )
    : null;

  const plotRow = el('div', { class: 'chart-plot-row' }, [
    yAxisLabels,
    el('div', { class: 'chart-plot-col' }, [
      el('div', { class: 'chart-plot' }, [chartSvg as unknown as Node]),
      el('figcaption', { class: 'chart-axis' }, [
        el('span', {}, [first]),
        el('span', {}, [lastLabel]),
      ]),
    ]),
  ]);

  return el('figure', { class: 'chart-fig' }, [plotRow]);
}

export interface BarRow {
  label: string;
  value: number;
  hint?: string;
  color?: string;
}

/** Horizontal labelled bars, each sized to value / max. */
export function barList(
  rows: BarRow[],
  opts: { format?: (n: number) => string } = {},
): HTMLElement {
  const fmt = opts.format ?? ((n) => n.toLocaleString('en-US'));
  const max = Math.max(1, ...rows.map((r) => r.value));
  return el(
    'div',
    { class: 'bars' },
    rows.map((r) =>
      el('div', { class: 'bar-row' }, [
        el('div', { class: 'bar-label', title: r.label }, [r.label]),
        el('div', { class: 'bar-track' }, [
          el('div', {
            class: 'bar-fill',
            style: `width: ${(r.value / max) * 100}%; background: ${
              r.color ?? 'var(--accent)'
            };`,
          }),
        ]),
        el('div', { class: 'bar-value' }, [r.hint ?? fmt(r.value)]),
      ]),
    ),
  );
}

export interface Segment {
  label: string;
  value: number;
  color: string;
}

/** A single horizontal stacked bar with a legend. */
export function stackedBar(segments: Segment[]): HTMLElement {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  const bar = el(
    'div',
    { class: 'stack-bar' },
    total > 0
      ? segments
          .filter((s) => s.value > 0)
          .map((s) =>
            el('div', {
              class: 'stack-seg',
              title: `${s.label}: ${s.value.toLocaleString('en-US')}`,
              style: `width: ${(s.value / total) * 100}%; background: ${s.color};`,
            }),
          )
      : [el('div', { class: 'stack-seg stack-empty' })],
  );
  const legend = el(
    'div',
    { class: 'stack-legend' },
    segments.map((s) =>
      el('div', { class: 'legend-item' }, [
        el('span', { class: 'legend-dot', style: `background: ${s.color};` }),
        el('span', {}, [s.label]),
        el('span', { class: 'legend-value' }, [
          s.value.toLocaleString('en-US'),
        ]),
      ]),
    ),
  );
  return el('div', { class: 'stack' }, [bar, legend]);
}

export interface FunnelStep {
  label: string;
  value: number;
}

/**
 * Vertical funnel: bars narrowing by absolute value, with step conversion %.
 * Steps aren't guaranteed to be a strict subset of one another (some funnel
 * sources count each step's event independently rather than a single
 * sequential cohort), so both the fill width and the step-conversion % are
 * clamped to 100 — a later step can never render as "bigger than the funnel"
 * or ">100% converted", even if the raw counts say so.
 */
export function funnelChart(steps: FunnelStep[]): HTMLElement {
  const top = Math.max(1, steps[0]?.value ?? 1);
  return el(
    'div',
    { class: 'funnel' },
    steps.map((s, i) => {
      const prev = i === 0 ? s.value : steps[i - 1].value;
      const conv = i === 0 ? 1 : prev > 0 ? Math.min(1, s.value / prev) : 0;
      const fillPct = Math.min(100, (s.value / top) * 100);
      return el('div', { class: 'funnel-step' }, [
        el('div', { class: 'funnel-meta' }, [
          el('span', { class: 'funnel-label' }, [s.label]),
          el('span', { class: 'funnel-count' }, [
            s.value.toLocaleString('en-US'),
            i > 0
              ? el('span', { class: 'funnel-conv' }, [
                  ` ${(conv * 100).toFixed(0)}%`,
                ])
              : null,
          ]),
        ]),
        el('div', { class: 'funnel-track' }, [
          el('div', {
            class: 'funnel-fill',
            style: `width: ${fillPct}%;`,
          }),
        ]),
      ]);
    }),
  );
}

/** Tiny inline sparkline for KPI cards. */
export function sparkline(values: number[], color = 'var(--accent)'): SVGElement {
  const w = 100;
  const h = 28;
  const max = Math.max(1, ...values);
  const n = values.length;
  const pts = values
    .map((v, i) => {
      const x = n <= 1 ? w / 2 : (i / (n - 1)) * w;
      const y = h - (v / max) * (h - 2) - 1;
      return `${x},${y}`;
    })
    .join(' ');
  return svg(
    'svg',
    {
      viewBox: `0 0 ${w} ${h}`,
      preserveAspectRatio: 'none',
      class: 'sparkline',
    },
    [svg('polyline', { points: pts, stroke: color, class: 'chart-line' })],
  );
}
